import type { ActivationFence } from '@/types/automationPublications.ts';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { create_manual_run_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { AUTOMATION_RUNS_KEY, PROJECT_RUNS_KEY } from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';

import { useNavigate } from 'react-router-dom';
import {
  TriggerDatasetCreateRequests,
  TriggerDatasetCreateResponse,
  TriggerDatasetsCreateResponse,
} from '@/hooks/api/datasets/trigger/types.ts';
import { notifications } from '@mantine/notifications';
import { RelativeFile } from '@/types/common.ts';
import { reportException } from '@/services/sentry.ts';
import { ExecutionConfigRequest } from '@/types/runs.ts';
import { watchRun } from '@/features/runBrowserNotifications/watchedRuns.ts';
import {
  getSignedUploadConfig,
  normalizeUploadContentType,
  relativeFileToUploadMetadata,
} from '@/hooks/api/datasets/uploadMetadata.ts';

export enum TriggerDatasetType {
  Text = 'text',
  File = 'file',
  Directory = 'directory',
  Existing = 'existing',
  Json = 'json',
}

export type TriggerNonDirectoryDataset = {
  // name: string;
  file?: File;
  trigger_id: string;
  type: TriggerDatasetType.File;
};

export type TriggerTextDataset = {
  name: string;
  text: string;
  trigger_id: string;
  type: TriggerDatasetType.Text;
};

export type TriggerDirectoryDataset = {
  name: string;
  files: RelativeFile[];
  trigger_id: string;
  type: TriggerDatasetType.Directory;
};

export type TriggerExistingDataset = {
  // name: string;
  dataset_id: string;
  trigger_id: string;
  type: TriggerDatasetType.Existing;
};

export type TriggerJsonDataset = {
  text: string;
  trigger_id: string;
  type: TriggerDatasetType.Json;
};

export type TriggerInputDataset =
  | TriggerTextDataset
  | TriggerNonDirectoryDataset
  | TriggerDirectoryDataset
  | TriggerExistingDataset
  | TriggerJsonDataset;

export type props = {
  runName: string;
  triggerStepID: string;
  triggers: TriggerInputDataset[];
  executionConfig: ExecutionConfigRequest;
  expectedActivation?: ActivationFence;
};

export type ManualTriggerRequest = {
  run_name: string;
  trigger_step_id: string;
  triggers: TriggerDatasetCreateRequests[];
  execution_config: ExecutionConfigRequest;
  expected_activation?: ActivationFence;
};

export class ManualRunInputProcessingError extends Error {
  constructor(
    readonly automationId: string,
    readonly runId: string,
    cause: unknown,
  ) {
    super(
      'Run was created, but one or more input files did not finish uploading or preprocessing.',
      { cause },
    );
    this.name = 'ManualRunInputProcessingError';
  }
}

export type TriggerManualRunError = AxiosError | ManualRunInputProcessingError;

const INVALID_PREFIXES = ['~$', '.'];
const INVALID_SUFFIXES = ['.DS_Store'];

const triggerToRequest = (trigger: TriggerInputDataset): TriggerDatasetCreateRequests | null => {
  if (trigger.type === TriggerDatasetType.Text) {
    return {
      trigger_id: trigger.trigger_id,
      name: trigger.name, // if name is blank -> backend will use the trigger name
      path: trigger.name,
      content_type: 'text/plain',
    };
  }
  if (trigger.type === TriggerDatasetType.Json) {
    if (!trigger.text.trim()) {
      return null;
    }
    const parsed: unknown = JSON.parse(trigger.text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('JSON inputs must be a JSON object.');
    }
    return { trigger_id: trigger.trigger_id, data: parsed as Record<string, unknown> };
  }
  if (trigger.type === TriggerDatasetType.File) {
    if (!trigger.file) {
      return null;
    }
    return {
      trigger_id: trigger.trigger_id,
      name: trigger.file.name,
      path: trigger.file.name,
      content_type: trigger.file.type ? normalizeUploadContentType(trigger.file.type) : null,
    };
  } else if ('files' in trigger) {
    if (trigger.files.length === 0) {
      return null;
    }
    const filteredFiles = trigger.files.filter(
      ({ file }) =>
        !INVALID_PREFIXES.some((p) => file.name.startsWith(p)) &&
        !INVALID_SUFFIXES.some((s) => file.name.endsWith(s)),
    );
    return {
      trigger_id: trigger.trigger_id,
      name: trigger.name, // if name is blank -> backend will use the trigger name
      files: filteredFiles.map(relativeFileToUploadMetadata),
    };
  } else {
    return {
      trigger_id: trigger.trigger_id,
      dataset_id: trigger.dataset_id,
    };
  }
};

const makeRequest = (
  runName: string,
  triggerStepID: string,
  triggers: TriggerInputDataset[],
  executionConfig: ExecutionConfigRequest,
  expectedActivation?: ActivationFence,
): ManualTriggerRequest => {
  return {
    run_name: runName,
    trigger_step_id: triggerStepID,
    triggers: triggers.map(triggerToRequest).filter((t) => t !== null),
    execution_config: executionConfig,
    ...(expectedActivation ? { expected_activation: expectedActivation } : {}),
  };
};

const getSingleUploadUrl = (urls: Record<string, string>, label: string): string => {
  const url = Object.values(urls)[0];
  if (!url) {
    throw new Error(`Missing upload URL for ${label}`);
  }
  return url;
};

const useTriggerManualRun = (
  providedAutomationId?: string,
  options?: UseMutationOptions<TriggerDatasetsCreateResponse, TriggerManualRunError, props>,
): UseMutationResult<TriggerDatasetsCreateResponse, TriggerManualRunError, props> => {
  const apiClient = useApiClient();
  const detectedAutomationId = useAutomationId();
  const automationId = providedAutomationId || detectedAutomationId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      runName,
      triggerStepID,
      triggers,
      executionConfig,
      expectedActivation,
    }: props) => {
      const response = await apiClient.post<TriggerDatasetsCreateResponse>(
        create_manual_run_url(automationId),
        makeRequest(runName, triggerStepID, triggers, executionConfig, expectedActivation),
      );

      await queryClient.invalidateQueries({ queryKey: AUTOMATION_RUNS_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: PROJECT_RUNS_KEY });

      const unsupportedFiles = response.data.triggers.flatMap(
        (trigger) => trigger.unsupported_files,
      );

      if (unsupportedFiles.length > 0) {
        const fileTypes = unsupportedFiles.map((file) => file.split('.').pop());
        const uniqueFileTypes = Array.from(new Set(fileTypes));
        notifications.show({
          title: `Skipping ${unsupportedFiles.length} unsupported files`,
          message: `Skipped unsupported file types: ${uniqueFileTypes.join(', ')}`,
          color: 'yellow',
        });
      }

      const doAsync = async (triggerResponse: TriggerDatasetCreateResponse) => {
        const trigger = triggers.find((t) => t.trigger_id === triggerResponse.trigger_id);
        if (!trigger) {
          reportException(Error(`No match found for trigger_id: ${triggerResponse.trigger_id}`));
          return;
        }
        if (
          trigger.type === TriggerDatasetType.Text &&
          triggerResponse.urls &&
          triggerResponse.dataset
        ) {
          const presignedUrl = getSingleUploadUrl(
            triggerResponse.urls,
            trigger.name || 'text input',
          );
          const blob = new Blob([trigger.text], { type: 'text/plain' });
          await axios.put(presignedUrl, blob, { headers: { 'Content-Type': 'text/plain' } });
        } else if ('file' in trigger && triggerResponse.urls && triggerResponse.dataset) {
          const presignedUrl = getSingleUploadUrl(triggerResponse.urls, trigger.file!.name);
          const contentType = trigger.file?.type
            ? normalizeUploadContentType(trigger.file.type)
            : null;
          await axios.put(presignedUrl, trigger.file, getSignedUploadConfig(contentType));
        } else if ('files' in trigger && triggerResponse.urls && triggerResponse.dataset) {
          // Start all uploads in parallel
          const uploadPromises = trigger.files
            .map((relativeFile) => {
              const uploadMetadata = relativeFileToUploadMetadata(relativeFile);
              if (triggerResponse.unsupported_files.includes(uploadMetadata.path)) {
                return null;
              }
              const url = triggerResponse.urls[uploadMetadata.path];
              if (!url) {
                throw new Error(`Missing upload URL for ${uploadMetadata.path}`);
              }
              return axios.put(
                url,
                relativeFile.file,
                getSignedUploadConfig(uploadMetadata.content_type),
              );
            })
            .filter((p): p is Promise<AxiosResponse> => Boolean(p));

          // Wait for ALL uploads to finish
          await Promise.all(uploadPromises);
        }
        await apiClient.put(`/datasets/${triggerResponse.dataset.id}/process`, {});
      };

      const triggerProcessingResults = await Promise.allSettled(
        response.data.triggers.map((trigger) => doAsync(trigger)),
      );
      const triggerProcessingError = triggerProcessingResults.find(
        (result) => result.status === 'rejected',
      );

      if (triggerProcessingError) {
        const manualRunError = new ManualRunInputProcessingError(
          automationId,
          response.data.run.id,
          triggerProcessingError.reason,
        );
        reportException(manualRunError, {
          extra: {
            automationId,
            runId: response.data.run.id,
            failedTriggerCount: triggerProcessingResults.filter(
              (result) => result.status === 'rejected',
            ).length,
            phase: 'manual_run_input_upload_or_process',
          },
        });
        notifications.show({
          title: 'Run created, but input upload failed',
          message: `Run ${response.data.run.id} was created. Open the run to review it, or create a new run and re-upload the inputs.`,
          color: 'red',
        });
        navigate(`/automations/${automationId}/runs/${response.data.run.id}`);
        throw manualRunError;
      }

      watchRun({
        automationId,
        run: response.data.run,
      });
      navigate(`/automations/${automationId}/runs/${response.data.run.id}`);

      return response.data;
    },
    ...options,
  });
};

export default useTriggerManualRun;
